import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GitCompare, ArrowRight, History } from 'lucide-react';

// Human labels for the leaf keys we compare — everything else falls back to
// a slug-to-title formatter. This keeps the diff readable without being an
// exhaustive schema.
const LABELS = {
  'commercial_offer.deal_structure': 'Deal Structure',
  'commercial_offer.base_amount': 'Base Amount',
  'commercial_offer.amount': 'Amount (legacy)',
  'commercial_offer.recurring_amount': 'Recurring Fee',
  'commercial_offer.signon_bonus_amount': 'Sign-On Bonus',
  'commercial_offer.signon_bonus_currency': 'Sign-On Bonus Currency',
  'commercial_offer.signon_bonus_timing': 'Sign-On Bonus Timing',
  'commercial_offer.payment_model': 'Payment Model',
  'commercial_offer.currency': 'Currency',
  'commercial_offer.response_deadline': 'Response Deadline',
  'rights_terms.exclusivity': 'Exclusivity',
  'rights_terms.exclusivity_categories': 'Exclusive Categories',
  'rights_terms.territory': 'Territory',
  'rights_terms.duration_type': 'Duration Type',
  'rights_terms.term_start': 'Term Start',
  'rights_terms.term_end': 'Term End',
  'rights_terms.expiry_behavior': 'Expiry Behavior',
  'rights_terms.sublicensing': 'Sublicensing',
  'rights_terms.generation_types': 'Generation Types',
  'rights_terms.visual_representation': 'Visual Representation',
  'rights_terms.permitted_alterations': 'Permitted Alterations',
  'rights_terms.dialogue_permitted': 'Voice Synthesis',
  'rights_terms.dialogue_scripted_only': 'Voice · Scripted Only',
  'rights_terms.statements_permitted': 'Creator Statements',
  'rights_terms.statement_scope': 'Statement Scope',
  'rights_terms.post_generation_modifications': 'Post-Gen Modifications',
  'rights_terms.purpose_of_use': 'Purpose of Use',
  'rights_terms.usage_scope': 'Usage Scope',
  'rights_terms.content_restrictions': 'Content Restrictions',
  'rights_terms.competitor_restriction': 'Competitor Restriction',
  'rights_terms.competitor_names': 'Competitor Names',
  'rights_terms.output_reuse': 'Output Reuse',
  'rights_terms.source_material_reuse': 'Source Material Reuse',
  'rights_terms.endorsement_requested': 'Endorsement',
  'rights_terms.asset_ownership': 'Asset Ownership',
  'rights_terms.creator_portfolio_rights': 'Portfolio Rights',
  'rights_terms.moral_rights': 'Moral Rights',
  'rights_terms.subscription_billing_frequency': 'Billing Frequency',
  'rights_terms.generation_caps_enabled': 'Generation Caps',
  'rights_terms.content_reuse_scope': 'Content Reuse Scope',
  'rights_terms.subscription_exclusivity': 'Subscription Exclusivity',
  'rights_terms.suspension_permitted': 'Suspension Permitted',
  'rights_terms.suspension_max_days': 'Suspension Max Days',
  'rights_terms.suspension_period_basis': 'Suspension Period',
  'rights_terms.price_change_policy': 'Price Change Policy',
  'rights_terms.agency_cancellation_notice_days': 'Agency Cancellation Notice',
  'rights_terms.agency_cancellation_fee': 'Agency Cancellation Fee',
  'rights_terms.creator_withdrawal_notice_days': 'Creator Withdrawal Notice',
  'rights_terms.accumulated_assets_post_subscription': 'Assets After Subscription',
  'rights_terms.termination_triggers': 'Termination Triggers',
  'rights_terms.cure_period': 'Cure Period',
  'rights_terms.morality_clause': 'Morality Clause',
  'rights_terms.morality_conditions': 'Morality Conditions',
  'rights_terms.early_termination_permitted': 'Early Termination',
  'rights_terms.early_termination_notice_days': 'Early Termination Notice',
  'rights_terms.early_termination_compensation': 'Early Termination Compensation',
  'rights_terms.early_termination_kill_fee_percentage': 'Kill Fee %',
  'rights_terms.post_termination_takedown_days': 'Takedown Window',
  'rights_terms.post_termination_deletion_days': 'Deletion Window',
  'rights_terms.residual_use_permitted': 'Residual Use',
  'rights_terms.residual_use_days': 'Residual Use Days',
  'rights_terms.residual_use_channels': 'Residual Use Channels',
  'rights_terms.deletion_confirmation_required': 'Deletion Confirmation',
  'rights_terms.governing_law': 'Governing Law',
  'rights_terms.dispute_resolution': 'Dispute Resolution',
  'campaign_brief.description': 'Campaign Description',
  'campaign_brief.objective': 'Campaign Objective',
  'campaign_brief.deliverable_types': 'Deliverables',
  'campaign_brief.channels': 'Channels',
  'likeness_request.standard_pack_included': 'Standard Pack',
  'likeness_request.additional_requirements': 'Additional Requirements',
  'likeness_request.custom_notes': 'Likeness Notes',
};

// Fields we intentionally hide from the diff — noise or duplicates of a
// friendlier field we already show.
const HIDDEN_KEYS = new Set([
  'sent_at', 'sent_by', 'updated_at', 'created_at', 'accepted_at',
  'rights_terms.ai_summary', 'rights_terms.plain_language_summary',
]);

function isPrimitive(v) {
  return v === null || v === undefined || typeof v !== 'object';
}

function shallowEqualArrays(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].map(x => String(x)).sort();
  const sb = [...b].map(x => String(x)).sort();
  return sa.every((v, i) => v === sb[i]);
}

// Compute a flat list of {path, oldValue, newValue} diffs between two
// proposals. Objects are recursed; arrays of primitives are compared as
// sorted sets; anything else is compared with strict equality.
function computeDiff(oldObj, newObj, path = '') {
  const diffs = [];
  const oldO = oldObj || {};
  const newO = newObj || {};
  const keys = new Set([...Object.keys(oldO), ...Object.keys(newO)]);
  for (const k of keys) {
    if (k.startsWith('_')) continue;
    const nextPath = path ? `${path}.${k}` : k;
    if (HIDDEN_KEYS.has(nextPath)) continue;
    const ov = oldO[k];
    const nv = newO[k];

    // Arrays of primitives → set comparison
    if (Array.isArray(ov) || Array.isArray(nv)) {
      if (!shallowEqualArrays(ov, nv)) {
        diffs.push({ path: nextPath, old: ov, new: nv });
      }
      continue;
    }
    // Both objects → recurse
    if (!isPrimitive(ov) && !isPrimitive(nv)) {
      diffs.push(...computeDiff(ov, nv, nextPath));
      continue;
    }
    // Object → primitive (or vice versa) → treat as leaf change
    if (!isPrimitive(ov) || !isPrimitive(nv)) {
      diffs.push({ path: nextPath, old: ov, new: nv });
      continue;
    }
    if (ov !== nv) diffs.push({ path: nextPath, old: ov, new: nv });
  }
  return diffs;
}

function labelFor(path) {
  return LABELS[path] || path.replace(/_/g, ' ').replace(/\./g, ' · ');
}

function fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.map(x => String(x).replace(/_/g, ' ')).join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s.replace(/_/g, ' ');
}

function timeLabel(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

export default function ProposalDiffViewer({ open, onClose, versions = [], currentProposal }) {
  // Versions come from deal.proposal_versions[] — a chronological list of
  // snapshots. We also treat the deal's *current* proposal as the newest
  // pseudo-version so the diff can always show "vs current".
  const chronological = useMemo(() => {
    const list = versions.map((v) => ({
      key: `v${v.version ?? ''}`,
      label: `Version ${v.version ?? '?'}`,
      when: v.snapshot_at,
      action: v.action,
      proposal: v.proposal || {},
    }));
    if (currentProposal) {
      list.push({
        key: 'current',
        label: 'Current',
        when: currentProposal.updated_at || currentProposal.sent_at,
        action: 'current',
        proposal: currentProposal,
      });
    }
    return list;
  }, [versions, currentProposal]);

  // Pick the two most-recent versions that actually differ (so the modal
  // doesn't open on 'identical' when the latest snapshot matches Current).
  const defaultPair = useMemo(() => {
    const n = chronological.length;
    if (n < 2) return { left: 0, right: Math.max(0, n - 1) };
    for (let right = n - 1; right > 0; right--) {
      for (let left = right - 1; left >= 0; left--) {
        const diffs = computeDiff(chronological[left].proposal, chronological[right].proposal);
        if (diffs.length > 0) return { left, right };
      }
    }
    // All versions identical → just show the two most recent so the empty
    // state gives context to the user.
    return { left: n - 2, right: n - 1 };
  }, [chronological]);

  const [rightIdx, setRightIdx] = useState(defaultPair.right);
  const [leftIdx, setLeftIdx] = useState(defaultPair.left);

  // Re-sync the default pair whenever the incoming versions change (e.g. the
  // modal is reopened for a different deal, or a new snapshot lands while it's
  // open). Users can still override — their explicit selection wins until the
  // versions list itself changes.
  useEffect(() => {
    setLeftIdx(defaultPair.left);
    setRightIdx(defaultPair.right);
  }, [defaultPair]);

  const left = chronological[leftIdx];
  const right = chronological[rightIdx];
  const diffs = useMemo(() => (left && right ? computeDiff(left.proposal, right.proposal) : []), [left, right]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="proposal-diff-viewer">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <GitCompare className="w-4 h-4 text-blue-600" /> Compare Proposal Versions
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Every send / resubmit is snapshotted. Pick any two versions to see exactly what changed.
          </DialogDescription>
        </DialogHeader>

        {/* Version pickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4 bg-slate-50 border-b border-slate-100 shrink-0">
          <VersionSelector
            label="From"
            testid="diff-left-selector"
            versions={chronological}
            currentIdx={leftIdx}
            onSelect={setLeftIdx}
          />
          <VersionSelector
            label="To"
            testid="diff-right-selector"
            versions={chronological}
            currentIdx={rightIdx}
            onSelect={setRightIdx}
          />
        </div>

        {/* Diff body */}
        <div className="flex-1 overflow-auto px-6 py-4" data-testid="diff-body">
          {!left || !right || leftIdx === rightIdx ? (
            <p className="text-sm text-slate-500 text-center py-8">
              Pick two different versions to compare.
            </p>
          ) : diffs.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <History className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-slate-800">These versions are identical.</p>
              <p className="text-xs text-slate-500 mt-1">No fields differ between {left.label} and {right.label}.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-[11px] text-slate-500 mb-2" data-testid="diff-summary">
                <span className="font-semibold text-slate-700">{diffs.length}</span> field{diffs.length === 1 ? '' : 's'} changed
                {' '}between <span className="font-medium text-slate-700">{left.label}</span>
                {' '}and <span className="font-medium text-slate-700">{right.label}</span>.
              </div>
              {diffs.map((d) => (
                <div key={d.path} data-testid={`diff-row-${d.path}`}
                  className="grid grid-cols-1 md:grid-cols-[minmax(0,220px)_1fr_auto_1fr] items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                  <p className="text-xs font-semibold text-slate-700 capitalize">{labelFor(d.path)}</p>
                  <div className="text-xs bg-red-50/60 border border-red-100 text-red-700 rounded px-2.5 py-1.5 break-words min-w-0">
                    <span className="text-[9px] uppercase font-bold text-red-500/70 block mb-0.5">From</span>
                    {fmt(d.old)}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300 justify-self-center hidden md:block" />
                  <div className="text-xs bg-emerald-50/60 border border-emerald-100 text-emerald-800 rounded px-2.5 py-1.5 break-words min-w-0">
                    <span className="text-[9px] uppercase font-bold text-emerald-600/70 block mb-0.5">To</span>
                    {fmt(d.new)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-diff-viewer">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionSelector({ label, versions, currentIdx, onSelect, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold text-slate-400 mb-1.5 tracking-wider">{label}</p>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {versions.map((v, i) => {
          const active = i === currentIdx;
          return (
            <button key={v.key} type="button"
              onClick={() => onSelect(i)}
              data-testid={`${testid}-${v.key}`}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors border ${
                active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}>
              <span>{v.label}</span>
              {v.action && v.action !== 'current' && (
                <Badge variant="outline" className={`text-[9px] capitalize border-0 px-1 py-0 ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {String(v.action).replace(/_/g, ' ')}
                </Badge>
              )}
              <span className={`text-[9px] ${active ? 'text-white/80' : 'text-slate-400'}`}>· {timeLabel(v.when)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
